import { useCallback } from 'react';
import { useReadResource } from '@/hooks/useReadResource';
import ReadNotice from '@/components/feature/ReadNotice';
import { metricPercent, riskLabel } from '@/lib/agentPresentation';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import { followStatusConfig, type FollowRecord, type FollowStatus } from '@/lib/brokerAccounts';
import { copyResultLabel } from '@/lib/decisionPresentation';
import {
  forexApi,
  toBrokerAccount,
  toFollowRecord,
  toUiAgentId,
  type AiDecisionDTO,
  type PlatformAgentDTO,
} from '@/services/forexApi';

const strategyLabels: Record<string, string> = {
  trend: '趋势跟踪',
  trend_following: '趋势跟踪',
  scalping: '高频剥头皮',
  grid: '网格策略',
  arbitrage: '套利策略',
  mean_reversion: '均值回归',
  breakout: '突破策略',
};

const riskLabels: Record<string, { label: string; color: string }> = {
  conservative: { label: '稳健', color: '#3B82F6' },
  moderate: { label: '均衡', color: '#F59E0B' },
  aggressive: { label: '激进', color: '#EF4444' },
  standard: { label: '标准', color: '#3B82F6' },
  strict: { label: '严格', color: '#F59E0B' },
};

const decisionActionStyles: Record<string, { bg: string; text: string; icon: string }> = {
  BUY: { bg: 'bg-green-50', text: 'text-green-600', icon: 'ri-arrow-up-line' },
  SELL: { bg: 'bg-red-50', text: 'text-red-600', icon: 'ri-arrow-down-line' },
  HOLD: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'ri-pause-line' },
  CLOSE: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'ri-close-line' },
};

const agentColors = ['#6366F1', '#10B981', '#F59E0B', '#8B5CF6', '#0EA5E9'];

function formatSymbol(symbol: string): string {
  if (!symbol) return '-';
  if (symbol.includes('/')) return symbol;
  if (symbol.length === 6) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  if (symbol === 'XAUUSD') return 'XAU/USD';
  return symbol;
}

function formatTime(value: string): string {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return value;
  return time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

function resolveAgentColor(agentId: number | string): string {
  const index = typeof agentId === 'number'
    ? Math.abs(agentId - 1) % agentColors.length
    : [...agentId].reduce((sum, char) => sum + char.charCodeAt(0), 0) % agentColors.length;
  return agentColors[index] || agentColors[0];
}

function isActiveFollow(status: FollowStatus): boolean {
  return ['running', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'pending_risk_confirm', 'configuring'].includes(status);
}

export default function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const validation = searchParams.get('validation') === '1';
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const agentId = id || '1';
  const uiAgentId = toUiAgentId(agentId);
  const agentColor = resolveAgentColor(uiAgentId);
  const identity = `${accessToken || ''}:${agentId}:${validation}`;
  const main = useReadResource(identity, useCallback(async () => {
    try { return await (validation ? forexApi.getValidationAgent(agentId, accessToken) : forexApi.getPlatformAgent(agentId, accessToken)); }
    catch (error) {
      if (!validation && error instanceof ApiError && error.status === 404) return forexApi.getValidationAgent(agentId, accessToken);
      throw error;
    }
  }, [accessToken, agentId, validation]), !!user && !!accessToken);
  const context = useReadResource(identity, useCallback(async () => {
    const [accountDtos, relations] = await Promise.all([forexApi.listFollowerAccounts(accessToken), forexApi.listFollowRelations(accessToken)]);
    const accounts = accountDtos.map(toBrokerAccount);
    return { accounts, follows: relations.map(relation => toFollowRecord(relation, accounts)) };
  }, [accessToken]), !!user && !!accessToken);
  const decisions = useReadResource(identity, useCallback(() => forexApi.listAiDecisions(accessToken, 1, 20), [accessToken]), !!user && !!accessToken && !validation);
  const agent = main.data;
  const loading = main.loading && !agent;
  const hasBound = context.data?.accounts.some(account => account.accountStatus === 'connected');
  const followStatus = context.data?.follows.find((follow: FollowRecord) => follow.agentId === uiAgentId && isActiveFollow(follow.followStatus))?.followStatus || 'not_started';
  const recentDecisions: AiDecisionDTO[] = decisions.data?.items.filter(decision => toUiAgentId(decision.agent_id) === uiAgentId).slice(0, 5) || [];
  const mainError = main.error instanceof ApiError ? main.error.status : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-gray-50 px-5 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
          <i className="ri-robot-2-line text-gray-400 text-2xl" />
        </div>
        <h1 className="text-gray-900 font-bold text-lg">{mainError === 404 ? '该策略不存在或当前不可访问' : mainError === 403 ? '暂无权限查看该策略' : mainError === 401 || !user ? '请登录后查看策略' : '策略读取失败'}</h1>
        <p className="text-gray-400 text-sm mt-2">请核对登录状态或重试读取。</p>
        <button className="mt-4 text-indigo-600" onClick={() => void main.refresh()}>重试</button>
        <button onClick={() => navigate('/agent')} className="mt-5 px-5 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold">
          返回列表
        </button>
      </div>
    );
  }

  const risk = { ...(riskLabels[agent.risk_level] || { color: '#6B7280' }), label: riskLabel(agent.risk_level) };
  const statusCfg = followStatusConfig[followStatus];
  const copyConfigPath = `/copy-config?agentId=${uiAgentId}${agent.is_validation ? '&validation=1' : ''}`;

  const getCTA = (): { label: string; action: () => void; style: 'gradient' | 'outline' | 'disabled'; disabled: boolean } => {
    if (!user) {
      return { label: '登录后跟随', action: () => navigate('/auth', { state: { from: `/agent/${agentId}` } }), style: 'gradient', disabled: false };
    }
    if (main.loading || main.error) {
      return { label: '策略状态待核对，请重试', action: () => { void main.refresh(); }, style: 'outline', disabled: main.loading };
    }
    if (context.loading || context.error || !context.data) {
      return { label: '账户状态暂未读取，重试后继续', action: () => { void context.refresh(); }, style: 'outline', disabled: context.loading };
    }
    if (!hasBound) {
      return { label: '先绑定账户', action: () => navigate('/broker'), style: 'gradient', disabled: false };
    }
    switch (followStatus) {
      case 'running':
        return { label: '管理跟单', action: () => navigate(copyConfigPath), style: 'outline', disabled: false };
      case 'paused_by_user':
        return { label: '管理跟单（已暂停）', action: () => navigate(copyConfigPath), style: 'outline', disabled: false };
      case 'paused_by_platform':
        return { label: '平台暂停中', action: () => {}, style: 'disabled', disabled: true };
      case 'agent_unavailable':
        return { label: '智能体暂不可用', action: () => {}, style: 'disabled', disabled: true };
      case 'status_unknown':
        return { label: '跟随状态待核对', action: () => {}, style: 'disabled', disabled: true };
      case 'configuring':
      case 'pending_risk_confirm':
        return { label: '继续配置', action: () => navigate(copyConfigPath), style: 'gradient', disabled: false };
      default:
        return { label: '跟随智能体', action: () => navigate(copyConfigPath), style: 'gradient', disabled: false };
    }
  };

  const cta = getCTA();

  return (
    <div className="min-h-screen bg-gray-50 pb-48">
      <div className="relative px-5 pt-12 pb-5" style={{ background: `linear-gradient(135deg, ${agentColor} 0%, ${agentColor}cc 100%)` }}>
        <button onClick={() => navigate('/agent')} className="flex items-center gap-1 text-white/80 text-sm mb-4">
          <i className="ri-arrow-left-s-line text-xl" /> 返回
        </button>
        <div className="flex items-center gap-3">
          {agent.is_validation && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">验证中 · 仅测试账户可跟随</span>}
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center relative">
            <i className="ri-robot-2-fill text-white text-2xl" />
            {followStatus === 'running' && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-400 border-2 border-white flex items-center justify-center">
                <i className="ri-check-line text-white" style={{ fontSize: '9px' }} />
              </div>
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">{agent.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full">
                {strategyLabels[agent.strategy_type] || agent.strategy_type}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${risk.color}30`, color: 'white' }}>
                {risk.label}
              </span>
              {followStatus !== 'not_started' && followStatus !== 'stopped' && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-white/20 text-white">
                  {statusCfg.label}
                </span>
              )}
            </div>
          </div>
        </div>

        {!agent.is_validation && <div className="grid grid-cols-4 gap-3 mt-4">
          {[
            { label: '累计收益', value: metricPercent(agent.total_return, true) },
            { label: '胜率', value: metricPercent(agent.win_rate) },
            { label: '最大回撤', value: metricPercent(agent.max_drawdown) },
            { label: '跟随人数', value: '暂未提供' },
          ].map((item) => (
            <div key={item.label} className="bg-white/15 rounded-xl p-2.5 text-center">
              <p className="text-white font-bold text-sm">{item.value}</p>
              <p className="text-white/60 text-[10px]">{item.label}</p>
            </div>
          ))}
        </div>}
        {!agent.is_validation && [agent.total_return, agent.win_rate, agent.max_drawdown].some(value => value == null) && <p className="mt-2 text-xs text-white/70">业绩数据暂未提供</p>}
      </div>

      <div className="px-5 mt-4">
        <ReadNotice {...main} label="策略" onRetry={main.refresh} />
        <ReadNotice {...context} label="账户与跟随" onRetry={context.refresh} />
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-file-text-line text-gray-400 text-sm" />
            <span className="text-gray-500 text-xs font-medium">策略说明</span>
          </div>
          <p className="text-gray-600 text-xs leading-relaxed">
            {strategyLabels[agent.strategy_type] || agent.strategy_type}策略，支持 {agent.supported_symbols.map(formatSymbol).join('、')}。{agent.is_validation ? '当前处于审核前验证，仅已授权测试账户可跟随。' : '历史表现仅用于跟单前评估，不代表未来收益。'}
          </p>
        </div>
      </div>

      <div className="px-5 mt-3">
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-settings-4-line text-sm" style={{ color: agentColor }} />
            <span className="text-gray-500 text-xs font-medium">服务端配置</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <InfoCell label="智能体状态" value={agent.is_validation ? '验证中' : agent.status === 'available' ? '可跟随' : '暂不可跟随'} />
            <InfoCell label="风险等级" value={risk.label} />
            <InfoCell label="策略类型" value={strategyLabels[agent.strategy_type] || agent.strategy_type} />
            <InfoCell label={agent.is_validation ? '验证周期' : '生效周期'} value={agent.strategy_timeframes?.length
              ? agent.strategy_timeframes.map((item) => `${formatSymbol(item.symbol)} · ${item.timeframe}`).join('、') : '—'} />
            <InfoCell label="支持品种" value={`${agent.supported_symbols.length} 个`} />
          </div>
        </div>
      </div>

      <div className="px-5 mt-4 text-xs text-gray-500">本人实际盈亏请查看资产页；策略决策本身不代表 Broker 成交或收益。</div>

      {!agent.is_validation && <div className="px-5 mt-3 mb-4">
        <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-flashlight-line text-sm" style={{ color: agentColor }} />
              <span className="text-gray-900 font-semibold text-sm">近期决策摘要</span>
            </div>
            <button onClick={() => navigate('/ai-decisions')} className="text-indigo-600 text-xs">查看全部</button>
          </div>
          <ReadNotice {...decisions} label="近期决策" onRetry={decisions.refresh} />
          {!decisions.loading && !decisions.error && recentDecisions.length === 0 && (
            <div className="px-4 py-6 text-center text-gray-400 text-xs">当前读取范围内暂无该策略决策</div>
          )}
          <div className="flex flex-col">
            {recentDecisions.map((decision, idx) => {
              const ac = decisionActionStyles[decision.action] || decisionActionStyles.HOLD;
              const executed = ['filled', 'applied'].includes(decision.follower_execution?.status || '');
              return (
                <div key={decision.id}>
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${ac.bg}`}>
                      <i className={`${ac.icon} ${ac.text} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 text-sm font-medium">{formatSymbol(decision.symbol)}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ac.bg} ${ac.text}`}>{decision.action}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {decision.summary ? <span className="text-xs text-gray-500">{decision.summary}</span> : <>
                        <span className="text-gray-400 text-[10px]">置信度</span>
                        <div className="w-12 h-1 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${decision.confidence}%`, background: 'linear-gradient(90deg, #6366F1, #8B5CF6)' }} />
                        </div>
                        <span className="text-gray-500 text-[10px]">{decision.confidence}%</span></>}
                      </div>
                    </div>
                    <div className="max-w-28 flex-shrink-0 text-right">
                      <span className="text-gray-400 text-[10px]">{formatTime(decision.created_at)}</span>
                      <p className={`text-[10px] mt-0.5 font-medium ${executed ? 'text-green-500' : 'text-gray-400'}`}>
                        {decision.follower_execution
                          ? copyResultLabel(decision.follower_execution.status, decision.my_accounts?.map((account) => account.execution_status) || [])
                          : '未执行'}
                      </p>
                    </div>
                  </div>
                  {idx < recentDecisions.length - 1 && <div className="h-px mx-4 bg-gray-50" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>}

      <div className="fixed left-0 right-0 bg-white border-t border-gray-100 px-5 py-3 z-40" style={{ bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))' }}>
        <button
          onClick={cta.action}
          disabled={cta.disabled}
          className="w-full font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          style={
            cta.style === 'gradient'
              ? { background: `linear-gradient(135deg, ${agentColor}, ${agentColor}cc)`, color: 'white', boxShadow: `0 4px 16px ${agentColor}59` }
              : cta.style === 'outline'
                ? { background: `${agentColor}14`, color: agentColor, border: `1px solid ${agentColor}40` }
                : { background: '#F3F4F6', color: '#9CA3AF' }
          }
        >
          {cta.style === 'gradient' && <i className="ri-add-line" />}
          {cta.style === 'outline' && <i className="ri-settings-4-line" />}
          {cta.label}
        </button>
      </div>
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-2.5">
      <p className="text-gray-400 text-[9px]">{label}</p>
      <p className="text-gray-900 text-xs font-semibold mt-0.5 truncate">{value}</p>
    </div>
  );
}
